import { useState } from 'react';
import { css } from '@emotion/react';
import { Box, ButtonBase, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

// Format to "Jun 01, 02:00 PM"
const getFormattedDate = (date: string, grouping: string) =>
  new Date(date).toLocaleString(undefined, {
    year: grouping === 'year' ? '2-digit' : undefined,
    month: 'short',
    day: '2-digit',
    hour: grouping === 'day' ? '2-digit' : undefined,
    minute: grouping === 'day' ? '2-digit' : undefined,
  });

type MultiLineStatsChart = {
  datesData: {
    date: string;
    visitsCount: number;
    scansCount: number;
    distinctScansCount: number;
  }[];
  onChangeGrouping: (grouping: string) => void;
  grouping: string;
};
export function MultiLineStatsChart({ datesData, onChangeGrouping, grouping }: MultiLineStatsChart) {
  const styles = useStyles();
  const [showTotals, setShowTotals] = useState(true);
  const [showUniques, setShowUniques] = useState(true);
  const [showVisits, setShowVisits] = useState(true);

  const labels = datesData.map((d) => getFormattedDate(d.date, grouping));

  const series = [];
  if (showTotals) {
    series.push({
      data: datesData.map((d) => d.scansCount),
      label: 'Totals',
      color: '#4caf50',
      showMark: false,
    });
  }
  if (showUniques) {
    series.push({
      data: datesData.map((d) => d.distinctScansCount),
      label: 'Uniques',
      color: '#ff9800',
      showMark: false,
    });
  }
  if (showVisits) {
    series.push({
      data: datesData.map((d) => d.visitsCount),
      label: 'Visits',
      color: '#2196f3',
      showMark: false,
    });
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={2}>
        {/* Checkboxes */}
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={showTotals}
                onChange={(e) => setShowTotals(e.target.checked)}
                style={{ color: '#4caf50' }}
              />
            }
            label="Totals"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showUniques}
                onChange={(e) => setShowUniques(e.target.checked)}
                style={{ color: '#ff9800' }}
              />
            }
            label="Uniques"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showVisits}
                onChange={(e) => setShowVisits(e.target.checked)}
                style={{ color: '#2196f3' }}
              />
            }
            label="Visits"
          />
        </FormGroup>

        <Box sx={styles.groupingWrapper}>
          <ButtonBase onClick={() => onChangeGrouping('day')} sx={styles.groupingButton(grouping === 'day')}>
            Day
          </ButtonBase>
          <ButtonBase onClick={() => onChangeGrouping('month')} sx={styles.groupingButton(grouping === 'month')}>
            Month
          </ButtonBase>
          <ButtonBase onClick={() => onChangeGrouping('year')} sx={styles.groupingButton(grouping === 'year')}>
            Year
          </ButtonBase>
        </Box>
      </Box>

      <LineChart
        height={400}
        xAxis={[{ scaleType: 'point', data: labels }]}
        series={series}
        legend={{ hidden: true }}
      />
    </Box>
  );
}

const useStyles = () => {
  return {
    groupingWrapper: css`
      display: flex;
      gap: 8px;
    `,
    groupingButton: (isSelected: boolean) => css`
      border-bottom: ${isSelected ? '1px solid black' : 'none'};
      padding: 2px 10px;
      font-size: 16px;
    `,
  };
};
